import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCoins, faWallet, faArrowDown, faArrowUp, faSyncAlt,
  faPlus, faFilter, faChartLine, faClock, faExternalLinkAlt, faClipboard,
  faCheckCircle, faBalanceScale, faTimesCircle, faQuestionCircle,
  faExclamationTriangle, faSpinner, faRotate,
} from '@fortawesome/free-solid-svg-icons';
import {
  UiPageHeader,
  UiFilterPanel,
  UiStatsGrid,
  UiModal,
  UiButton,
} from '../../components/ui';
import type { StatCardDef } from '../../components/ui';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
const TZ = 'Asia/Bangkok';
const todayBKK = () => dayjs().tz(TZ).format('YYYY-MM-DD');
const get7DaysAgoStr = () => dayjs().tz(TZ).subtract(7, 'day').format('YYYY-MM-DD');

const FILTER_SELECT_CLASS =
  'w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';
const INPUT_CLASS =
  'w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';
const TEXTAREA_CLASS = `${INPUT_CLASS} resize-none`;
const LABEL_CLASS =
  'block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1';
const TABLE_ACTION_BTN =
  'px-2.5 py-1 rounded-lg border transition-colors text-xs font-medium inline-flex items-center gap-1 disabled:opacity-50';
const HEADER_GLASS_BTN =
  'px-4 py-2.5 rounded-xl text-sm font-medium border border-white/50 bg-white/25 hover:bg-white/40 text-white shadow-sm transition-colors flex items-center gap-2 disabled:opacity-50';
const CARD_CLASS = 'bg-white dark:bg-slate-800 rounded-xl shadow-theme';

interface UsdtWallet {
  id: string;
  address: string;
  label: string | null;
  network: string;
  is_active: boolean;
  last_synced_at: string | null;
  last_balance: number;
  created_by: string | null;
  created_at: string;
}

interface UsdtTransaction {
  id: string;
  wallet_id: string;
  tx_hash: string | null;
  direction: 'in' | 'out';
  amount: number;
  from_address: string | null;
  to_address: string | null;
  status: string;
  source: 'manual' | 'blockchain';
  block_timestamp: string | null;
  note: string | null;
  created_by: string | null;
  created_at: string;
  usdt_wallets: { label: string | null; address: string } | null;
}

interface ReconcileData {
  summary: {
    total_blockchain: number;
    total_manual: number;
    matched: number;
    mismatched: number;
    blockchain_only: number;
    manual_only: number;
  };
  matched: Array<{ blockchain: UsdtTransaction; manual: UsdtTransaction }>;
  mismatched: Array<{ blockchain: UsdtTransaction; manual: UsdtTransaction; diffs: Array<{ field: string; manual: any; blockchain: any }> }>;
  blockchain_only: UsdtTransaction[];
  manual_only: UsdtTransaction[];
}

interface DashboardData {
  wallets_count: number;
  total_usdt: number;
  rate_thb: number;
  total_thb: number;
  today: { in: number; out: number; net: number };
  week: { in: number; out: number; net: number };
  recent_transactions: UsdtTransaction[];
  wallets: UsdtWallet[];
}

const shortAddr = (addr: string) => addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : '-';

const DirectionBadge: React.FC<{ direction: 'in' | 'out' }> = ({ direction }) =>
  direction === 'in' ? (
    <span className="px-2.5 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs font-semibold inline-flex items-center gap-1">
      <FontAwesomeIcon icon={faArrowDown} /> IN
    </span>
  ) : (
    <span className="px-2.5 py-1 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full text-xs font-semibold inline-flex items-center gap-1">
      <FontAwesomeIcon icon={faArrowUp} /> OUT
    </span>
  );

const SourceBadge: React.FC<{ source: string }> = ({ source }) => (
  <span className={`px-2 py-0.5 rounded text-xs ${
    source === 'blockchain'
      ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
      : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300'
  }`}>
    {source}
  </span>
);

const fmtNum = (n: number, decimals = 2) =>
  n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

const fmtDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString('th-TH', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fmtTime = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const UsdtWalletPage: React.FC = () => {
  const [tab, setTab] = useState<'dashboard' | 'wallets' | 'transactions' | 'reconcile'>('dashboard');
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [wallets, setWallets] = useState<UsdtWallet[]>([]);
  const [transactions, setTransactions] = useState<UsdtTransaction[]>([]);
  const [txTotal, setTxTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [directionFilter, setDirectionFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [dateFrom, setDateFrom] = useState(get7DaysAgoStr);
  const [dateTo, setDateTo] = useState(todayBKK);
  const [appliedDateFrom, setAppliedDateFrom] = useState(get7DaysAgoStr);
  const [appliedDateTo, setAppliedDateTo] = useState(todayBKK);

  // Modals
  const [showAddWallet, setShowAddWallet] = useState(false);
  const [showRecordTx, setShowRecordTx] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Add wallet form
  const [newAddress, setNewAddress] = useState('');
  const [newLabel, setNewLabel] = useState('');

  // Record transaction form
  const [txWalletId, setTxWalletId] = useState('');
  const [txDirection, setTxDirection] = useState<'in' | 'out'>('in');
  const [txAmount, setTxAmount] = useState('');
  const [txNote, setTxNote] = useState('');
  const [txHash, setTxHash] = useState('');

  const [syncing, setSyncing] = useState(false);
  const [reconcileData, setReconcileData] = useState<ReconcileData | null>(null);
  const [reconciling, setReconciling] = useState(false);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);

  const initialLoadDone = React.useRef(false);

  const loadDashboard = useCallback(async () => {
    try {
      if (!initialLoadDone.current) setLoading(true);
      const res = await merchantApi.get('/admin/usdt-wallets/dashboard');
      setDashboard(res.data.data);
      setWallets(res.data.data.wallets || []);
      initialLoadDone.current = true;
    } catch {
      if (!initialLoadDone.current) toast.error('ไม่สามารถโหลด Dashboard ได้');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadWallets = useCallback(async () => {
    try {
      const res = await merchantApi.get('/admin/usdt-wallets');
      setWallets(res.data.data || []);
    } catch {
      toast.error('ไม่สามารถโหลด Wallets ได้');
    }
  }, []);

  const loadTransactions = useCallback(async (overrides?: { from?: string; to?: string; direction?: string; source?: string }) => {
    const from = overrides?.from ?? appliedDateFrom;
    const to = overrides?.to ?? appliedDateTo;
    const direction = overrides?.direction !== undefined ? overrides.direction : directionFilter;
    const source = overrides?.source !== undefined ? overrides.source : sourceFilter;
    try {
      setLoading(true);
      let url = '/admin/usdt-wallets/transactions?limit=50&offset=0';
      if (direction) url += `&direction=${direction}`;
      if (source) url += `&source=${source}`;
      if (from) url += `&start_date=${from}T00:00:00`;
      if (to) url += `&end_date=${to}T23:59:59`;
      const res = await merchantApi.get(url);
      setTransactions(res.data.data || []);
      setTxTotal(res.data.total || 0);
    } catch {
      toast.error('ไม่สามารถโหลด Transactions ได้');
    } finally {
      setLoading(false);
    }
  }, [directionFilter, sourceFilter, appliedDateFrom, appliedDateTo]);

  const handleTxSearch = () => {
    if (dayjs(dateFrom).isAfter(dayjs(dateTo), 'day')) {
      toast.error('วันที่เริ่มต้นต้องน้อยกว่าหรือเท่ากับวันที่สิ้นสุด');
      return;
    }
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    loadTransactions({ from: dateFrom, to: dateTo });
  };

  const handleTxReset = () => {
    const today = todayBKK();
    const weekAgo = get7DaysAgoStr();
    setDateFrom(weekAgo);
    setDateTo(today);
    setAppliedDateFrom(weekAgo);
    setAppliedDateTo(today);
    setDirectionFilter('');
    setSourceFilter('');
    loadTransactions({ from: weekAgo, to: today, direction: '', source: '' });
  };

  const dashboardStats: StatCardDef[] = useMemo(() => {
    if (!dashboard) return [];
    return [
      {
        icon: faCoins,
        iconClassName: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
        label: 'Total USDT',
        value: <span className="text-green-600 dark:text-green-400">{fmtNum(dashboard.total_usdt)}</span>,
        subValue: <span className="text-sm text-gray-400 dark:text-slate-500">~฿{fmtNum(dashboard.total_thb)}</span>,
      },
      {
        icon: faChartLine,
        iconClassName: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
        label: 'Rate USDT/THB',
        value: <span className="text-blue-600 dark:text-blue-400">฿{fmtNum(dashboard.rate_thb)}</span>,
        subValue: <span className="text-sm text-gray-400 dark:text-slate-500">{dashboard.wallets_count} wallets</span>,
      },
      {
        icon: faArrowDown,
        iconClassName: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
        label: 'Today In',
        value: <span className="text-green-600 dark:text-green-400">+{fmtNum(dashboard.today.in)}</span>,
        subValue: <span className="text-sm text-gray-400 dark:text-slate-500">USDT</span>,
      },
      {
        icon: faArrowUp,
        iconClassName: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
        label: 'Today Out',
        value: <span className="text-red-600 dark:text-red-400">-{fmtNum(dashboard.today.out)}</span>,
        subValue: <span className="text-sm text-gray-400 dark:text-slate-500">USDT</span>,
      },
    ];
  }, [dashboard]);

  useEffect(() => {
    loadDashboard();

    const interval = setInterval(() => {
      loadDashboard();
    }, 30000);

    return () => clearInterval(interval);
  }, [loadDashboard]);

  const loadReconcile = useCallback(async () => {
    try {
      setReconciling(true);
      const res = await merchantApi.get('/admin/usdt-wallets/reconcile');
      setReconcileData(res.data.data);
    } catch {
      toast.error('ไม่สามารถโหลดข้อมูล Reconcile ได้');
    } finally {
      setReconciling(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'wallets') loadWallets();
    if (tab === 'transactions') loadTransactions();
    if (tab === 'reconcile') loadReconcile();
  }, [tab, loadWallets, loadTransactions, loadReconcile]);

  const handleVerifyTx = async (transactionId: string) => {
    try {
      setVerifyingId(transactionId);
      const res = await merchantApi.post('/admin/usdt-wallets/verify-tx', { transaction_id: transactionId });
      const data = res.data.data;

      if (data.status === 'matched') {
        toast.success('ข้อมูลตรงกับ Blockchain');
      } else if (data.status === 'mismatched') {
        toast.warn(`ข้อมูลไม่ตรง: ${data.diffs?.map((d: any) => `${d.field} (manual: ${d.manual}, chain: ${d.blockchain})`).join(', ')}`);
      } else if (data.status === 'not_found') {
        toast.error('ไม่พบ TX Hash บน Blockchain');
      } else if (data.status === 'no_hash') {
        toast.info('รายการนี้ไม่มี TX Hash');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'ตรวจสอบไม่สำเร็จ');
    } finally {
      setVerifyingId(null);
    }
  };

  const handleSyncBlockchain = async () => {
    try {
      setSyncing(true);
      const res = await merchantApi.post('/admin/usdt-wallets/sync');
      const { wallets_synced, new_transactions } = res.data.data || {};
      toast.success(`Sync สำเร็จ: ${wallets_synced || 0} wallets, ${new_transactions || 0} new txs`);
      loadDashboard();
      if (tab === 'wallets') loadWallets();
      if (tab === 'transactions') loadTransactions();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Sync ล้มเหลว');
    } finally {
      setSyncing(false);
    }
  };

  const handleAddWallet = async () => {
    if (!newAddress) return toast.error('กรุณาระบุ address');
    try {
      setSubmitting(true);
      await merchantApi.post('/admin/usdt-wallets', { address: newAddress, label: newLabel || null });
      toast.success('เพิ่ม Wallet สำเร็จ');
      setShowAddWallet(false);
      setNewAddress('');
      setNewLabel('');
      loadDashboard();
      loadWallets();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เพิ่ม Wallet ล้มเหลว');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecordTx = async () => {
    if (!txWalletId || !txAmount) return toast.error('กรุณากรอกข้อมูลให้ครบ');
    try {
      setSubmitting(true);
      await merchantApi.post('/admin/usdt-wallets/transactions', {
        wallet_id: txWalletId,
        direction: txDirection,
        amount: parseFloat(txAmount),
        note: txNote || null,
        tx_hash: txHash || null,
      });
      toast.success('บันทึกรายการสำเร็จ');
      setShowRecordTx(false);
      setTxWalletId('');
      setTxAmount('');
      setTxNote('');
      setTxHash('');
      loadDashboard();
      if (tab === 'transactions') loadTransactions();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'บันทึกล้มเหลว');
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!', { autoClose: 1000 });
  };

  return (
    <div className="max-w-full">
      <UiPageHeader
        icon={faCoins}
        title="USDT Wallet Tracker"
        subtitle="TRC20 Tron Network"
        actions={
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleSyncBlockchain}
              disabled={syncing}
              className={HEADER_GLASS_BTN}
            >
              <FontAwesomeIcon icon={faSyncAlt} className={syncing ? 'animate-spin' : ''} />
              {syncing ? 'กำลัง Sync...' : 'Sync Blockchain'}
            </button>
            <button type="button" onClick={() => setShowRecordTx(true)} className={HEADER_GLASS_BTN}>
              <FontAwesomeIcon icon={faPlus} /> บันทึกรายการ
            </button>
            <UiButton icon={faWallet} onClick={() => setShowAddWallet(true)}>
              เพิ่ม Wallet
            </UiButton>
          </div>
        }
      />

      <div className="flex gap-1 mb-6 bg-gray-100 dark:bg-slate-800 rounded-xl p-1 max-w-lg">
        {(['dashboard', 'wallets', 'transactions', 'reconcile'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === t
                ? 'bg-white dark:bg-slate-700 shadow text-gray-800 dark:text-slate-100'
                : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300'
            }`}
          >
            {t === 'dashboard' ? 'Dashboard' : t === 'wallets' ? 'Wallets' : t === 'transactions' ? 'Transactions' : 'Reconcile'}
          </button>
        ))}
      </div>

      {/* ========== DASHBOARD TAB ========== */}
      {tab === 'dashboard' && (
        <>
          {loading && !dashboard ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-12 h-12 border-4 border-gray-200 dark:border-slate-600 border-t-gray-500 dark:border-t-slate-400 rounded-full animate-spin mb-4" />
              <p className="text-gray-400 dark:text-slate-500 text-sm">Loading...</p>
            </div>
          ) : dashboard && (
            <>
              <UiStatsGrid cards={dashboardStats} loading={loading} cols={4} />

              <div className={`${CARD_CLASS} p-5 mb-6`}>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-200 mb-3">สรุป 7 วัน</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <p className="text-xs text-gray-500 dark:text-slate-400">รับเข้า</p>
                    <p className="text-lg font-bold text-green-600 dark:text-green-400">+{fmtNum(dashboard.week.in)}</p>
                  </div>
                  <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <p className="text-xs text-gray-500 dark:text-slate-400">ส่งออก</p>
                    <p className="text-lg font-bold text-red-600 dark:text-red-400">-{fmtNum(dashboard.week.out)}</p>
                  </div>
                  <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-xs text-gray-500 dark:text-slate-400">คงเหลือ (net)</p>
                    <p className={`text-lg font-bold ${dashboard.week.net >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {dashboard.week.net >= 0 ? '+' : ''}{fmtNum(dashboard.week.net)}
                    </p>
                  </div>
                </div>
              </div>

              <div className={`${CARD_CLASS} overflow-hidden`}>
                <div className="px-5 py-4 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-200">รายการล่าสุด</h3>
                  <button type="button" onClick={() => setTab('transactions')} className="text-xs hover:underline" style={{ color: 'var(--theme-primary)' }}>
                    ดูทั้งหมด
                  </button>
                </div>
                {dashboard.recent_transactions.length === 0 ? (
                  <div className="py-12 text-center text-gray-400 dark:text-slate-500 text-sm">ยังไม่มีรายการ</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-slate-700/50 border-b border-gray-100 dark:border-slate-700">
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">Direction</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">Amount</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">Source</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">Wallet</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 dark:divide-slate-700">
                      {dashboard.recent_transactions.map((tx) => (
                        <tr key={tx.id} className="hover:bg-gray-50/50 dark:hover:bg-slate-700/30 transition-colors">
                          <td className="px-4 py-3"><DirectionBadge direction={tx.direction} /></td>
                          <td className="px-4 py-3 text-right">
                            <span className={`font-bold ${tx.direction === 'in' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                              {tx.direction === 'in' ? '+' : '-'}{fmtNum(parseFloat(String(tx.amount)))}
                            </span>
                          </td>
                          <td className="px-4 py-3"><SourceBadge source={tx.source} /></td>
                          <td className="px-4 py-3 text-xs text-gray-500 dark:text-slate-400">{tx.usdt_wallets?.label || shortAddr(tx.usdt_wallets?.address || '')}</td>
                          <td className="px-4 py-3">
                            <p className="text-sm text-gray-700 dark:text-slate-200">{fmtDate(tx.created_at)}</p>
                            <p className="text-xs text-gray-400 dark:text-slate-500">{fmtTime(tx.created_at)}</p>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* ========== WALLETS TAB ========== */}
      {tab === 'wallets' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {wallets.length === 0 ? (
            <div className="col-span-full py-20 text-center text-gray-400 dark:text-slate-500">
              <FontAwesomeIcon icon={faWallet} className="text-5xl text-gray-200 dark:text-slate-600 mb-4" /><br />
              <span className="text-sm">ยังไม่มี Wallet</span>
            </div>
          ) : wallets.map((w) => (
            <div key={w.id} className={`${CARD_CLASS} p-5 hover:shadow-lg transition-shadow`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 dark:text-green-400">
                    <FontAwesomeIcon icon={faWallet} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 dark:text-slate-100">{w.label || 'Unnamed'}</p>
                    <p className="text-xs text-gray-400 dark:text-slate-500">{w.network}</p>
                  </div>
                </div>
                <span className={`w-2.5 h-2.5 rounded-full ${w.is_active ? 'bg-green-400' : 'bg-gray-300 dark:bg-slate-600'}`} />
              </div>

              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3 mb-3">
                <div className="flex items-center justify-between">
                  <code className="text-xs text-gray-600 dark:text-slate-300 font-mono">{shortAddr(w.address)}</code>
                  <button type="button" onClick={() => copyToClipboard(w.address)}
                    className="text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 transition-colors">
                    <FontAwesomeIcon icon={faClipboard} className="text-xs" />
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">{fmtNum(parseFloat(String(w.last_balance)))}</p>
                  <p className="text-xs text-gray-400 dark:text-slate-500">USDT</p>
                </div>
                {w.last_synced_at && (
                  <div className="text-right">
                    <p className="text-xs text-gray-400 dark:text-slate-500 flex items-center gap-1 justify-end">
                      <FontAwesomeIcon icon={faClock} />
                      {fmtDate(w.last_synced_at)}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-slate-500">{fmtTime(w.last_synced_at)}</p>
                  </div>
                )}
              </div>

              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-slate-700">
                <a href={`https://tronscan.org/#/address/${w.address}`} target="_blank" rel="noreferrer"
                  className="text-xs hover:underline flex items-center gap-1" style={{ color: 'var(--theme-primary)' }}>
                  <FontAwesomeIcon icon={faExternalLinkAlt} /> ดูบน TronScan
                </a>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ========== TRANSACTIONS TAB ========== */}
      {tab === 'transactions' && (
        <>
          <UiFilterPanel
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFromChange={setDateFrom}
            onDateToChange={setDateTo}
            onSearch={handleTxSearch}
            onReset={handleTxReset}
            loading={loading}
            actions={
              <button
                type="button"
                onClick={() => loadTransactions()}
                disabled={loading}
                className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 hover:bg-gray-50 transition-colors text-gray-600 dark:border-slate-600 dark:hover:bg-slate-700 dark:text-slate-300 flex items-center gap-2 disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faRotate} className={loading ? 'animate-spin' : ''} /> Refresh
              </button>
            }
          >
            <div className="flex-1 min-w-[120px]">
              <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                <FontAwesomeIcon icon={faFilter} className="mr-1" /> Direction
              </label>
              <select value={directionFilter} onChange={(e) => setDirectionFilter(e.target.value)} className={FILTER_SELECT_CLASS}>
                <option value="">ทั้งหมด</option>
                <option value="in">IN</option>
                <option value="out">OUT</option>
              </select>
            </div>
            <div className="flex-1 min-w-[120px]">
              <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                <FontAwesomeIcon icon={faFilter} className="mr-1" /> Source
              </label>
              <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className={FILTER_SELECT_CLASS}>
                <option value="">ทั้งหมด</option>
                <option value="blockchain">Blockchain</option>
                <option value="manual">Manual</option>
              </select>
            </div>
          </UiFilterPanel>

          <div className={`${CARD_CLASS} overflow-hidden`}>
            <div className="px-5 py-3 border-b border-gray-100 dark:border-slate-700 text-xs text-gray-400 dark:text-slate-500">
              พบ {txTotal.toLocaleString()} รายการ
            </div>
            <div className="overflow-x-auto">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="w-12 h-12 border-4 border-gray-200 dark:border-slate-600 border-t-gray-500 dark:border-t-slate-400 rounded-full animate-spin mb-4" />
                  <p className="text-gray-400 dark:text-slate-500 text-sm">Loading...</p>
                </div>
              ) : transactions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <FontAwesomeIcon icon={faCoins} className="text-5xl text-gray-200 dark:text-slate-600 mb-4" />
                  <p className="text-gray-400 dark:text-slate-500 text-sm">ไม่มีรายการ</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-slate-700/50 border-b border-gray-100 dark:border-slate-700">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">Direction</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">Amount (USDT)</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">From / To</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">TX Hash</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">Source</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">Wallet</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">Note</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">Date</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">Verify</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 dark:divide-slate-700">
                    {transactions.map((tx) => (
                      <tr key={tx.id} className="hover:bg-gray-50/50 dark:hover:bg-slate-700/30 transition-colors">
                        <td className="px-4 py-3"><DirectionBadge direction={tx.direction} /></td>
                        <td className="px-4 py-3 text-right">
                          <span className={`font-bold ${tx.direction === 'in' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {tx.direction === 'in' ? '+' : '-'}{fmtNum(parseFloat(String(tx.amount)))}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-xs text-gray-500 dark:text-slate-400">From: {shortAddr(tx.from_address || '')}</p>
                          <p className="text-xs text-gray-500 dark:text-slate-400">To: {shortAddr(tx.to_address || '')}</p>
                        </td>
                        <td className="px-4 py-3">
                          {tx.tx_hash ? (
                            <a href={`https://tronscan.org/#/transaction/${tx.tx_hash}`} target="_blank" rel="noreferrer"
                              className="text-xs hover:underline font-mono" style={{ color: 'var(--theme-primary)' }}>
                              {tx.tx_hash.slice(0, 10)}...
                            </a>
                          ) : (
                            <span className="text-xs text-gray-300 dark:text-slate-600">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3"><SourceBadge source={tx.source} /></td>
                        <td className="px-4 py-3 text-xs text-gray-500 dark:text-slate-400">{tx.usdt_wallets?.label || shortAddr(tx.usdt_wallets?.address || '')}</td>
                        <td className="px-4 py-3 text-xs text-gray-500 dark:text-slate-400 max-w-[120px] truncate" title={tx.note || ''}>{tx.note || '-'}</td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-gray-700 dark:text-slate-200">{fmtDate(tx.created_at)}</p>
                          <p className="text-xs text-gray-400 dark:text-slate-500">{fmtTime(tx.created_at)}</p>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {tx.source === 'manual' && tx.tx_hash ? (
                            <button
                              type="button"
                              onClick={() => handleVerifyTx(tx.id)}
                              disabled={verifyingId === tx.id}
                              className={`${TABLE_ACTION_BTN} bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 border-blue-200 dark:border-blue-800`}
                            >
                              <FontAwesomeIcon icon={verifyingId === tx.id ? faSpinner : faBalanceScale} className={verifyingId === tx.id ? 'animate-spin' : ''} />
                              {verifyingId === tx.id ? '...' : 'Verify'}
                            </button>
                          ) : tx.source === 'blockchain' ? (
                            <span className="text-xs text-green-500 dark:text-green-400"><FontAwesomeIcon icon={faCheckCircle} /></span>
                          ) : (
                            <span className="text-xs text-gray-300 dark:text-slate-600">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}

      {/* ========== RECONCILE TAB ========== */}
      {tab === 'reconcile' && (
        <>
          <div className={`${CARD_CLASS} p-5 mb-6`}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-bold text-gray-800 dark:text-slate-100 flex items-center gap-2">
                  <FontAwesomeIcon icon={faBalanceScale} style={{ color: 'var(--theme-primary)' }} />
                  Reconciliation
                </h3>
                <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">เทียบรายการ Manual vs Blockchain — ดูว่ารายการไหนขาด/เกิน/ยอดไม่ตรง</p>
              </div>
              <UiButton icon={faSyncAlt} onClick={loadReconcile} disabled={reconciling} loading={reconciling}>
                {reconciling ? 'กำลังตรวจสอบ...' : 'ตรวจสอบใหม่'}
              </UiButton>
            </div>
          </div>

          {reconciling && !reconcileData ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-12 h-12 border-4 border-gray-200 dark:border-slate-600 border-t-gray-500 dark:border-t-slate-400 rounded-full animate-spin mb-4" />
              <p className="text-gray-400 dark:text-slate-500 text-sm">กำลังเทียบข้อมูล Manual vs Blockchain...</p>
            </div>
          ) : reconcileData ? (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
                <div className={`${CARD_CLASS} p-4 text-center`}>
                  <p className="text-xs text-gray-500 dark:text-slate-400 uppercase font-medium">Blockchain</p>
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{reconcileData.summary.total_blockchain}</p>
                </div>
                <div className={`${CARD_CLASS} p-4 text-center`}>
                  <p className="text-xs text-gray-500 dark:text-slate-400 uppercase font-medium">Manual</p>
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{reconcileData.summary.total_manual}</p>
                </div>
                <div className={`${CARD_CLASS} p-4 text-center border-2 border-green-200 dark:border-green-800`}>
                  <p className="text-xs text-green-600 dark:text-green-400 uppercase font-medium">Matched</p>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">{reconcileData.summary.matched}</p>
                </div>
                <div className={`${CARD_CLASS} p-4 text-center border-2 border-amber-200 dark:border-amber-800`}>
                  <p className="text-xs text-amber-600 dark:text-amber-400 uppercase font-medium">ยอดไม่ตรง</p>
                  <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{reconcileData.summary.mismatched}</p>
                </div>
                <div className={`${CARD_CLASS} p-4 text-center border-2 border-red-200 dark:border-red-800`}>
                  <p className="text-xs text-red-600 dark:text-red-400 uppercase font-medium">ขาด (ไม่มี Manual)</p>
                  <p className="text-2xl font-bold text-red-600 dark:text-red-400">{reconcileData.summary.blockchain_only}</p>
                </div>
                <div className={`${CARD_CLASS} p-4 text-center border-2 border-orange-200 dark:border-orange-800`}>
                  <p className="text-xs text-orange-600 dark:text-orange-400 uppercase font-medium">เกิน (Manual เท่านั้น)</p>
                  <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{reconcileData.summary.manual_only}</p>
                </div>
              </div>

              {/* ========== Mismatched Section ========== */}
              {reconcileData.mismatched.length > 0 && (
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-theme overflow-hidden mb-6">
                  <div className="px-5 py-3 border-b border-amber-100 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
                    <h4 className="text-sm font-bold text-amber-700 dark:text-amber-400 flex items-center gap-2">
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                      ยอดไม่ตรง ({reconcileData.mismatched.length} รายการ)
                    </h4>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">TX Hash ตรงกัน แต่จำนวนเงินหรือทิศทางต่างกัน</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-slate-700/50 border-b border-gray-100 dark:border-slate-700">
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">TX Hash</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">Wallet</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">Manual</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">Blockchain</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">ผลต่าง</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">สาเหตุ</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50 dark:divide-slate-700">
                        {reconcileData.mismatched.map((r, idx) => {
                          const manualAmt = parseFloat(String(r.manual.amount));
                          const bcAmt = parseFloat(String(r.blockchain.amount));
                          return (
                            <tr key={idx} className="bg-amber-50/30 dark:bg-amber-900/10 hover:bg-amber-50/50 dark:hover:bg-amber-900/20 transition-colors">
                              <td className="px-4 py-3">
                                <a href={`https://tronscan.org/#/transaction/${r.blockchain.tx_hash}`} target="_blank" rel="noreferrer"
                                  className="text-xs hover:underline font-mono" style={{ color: 'var(--theme-primary)' }}>
                                  {r.blockchain.tx_hash?.slice(0, 14)}...
                                </a>
                              </td>
                              <td className="px-4 py-3 text-xs text-gray-500 dark:text-slate-400">
                                {r.blockchain.usdt_wallets?.label || shortAddr(r.blockchain.usdt_wallets?.address || '')}
                              </td>
                              <td className="px-4 py-3 text-right font-bold text-blue-600">
                                {r.manual.direction === 'in' ? '+' : '-'}{fmtNum(manualAmt)}
                              </td>
                              <td className="px-4 py-3 text-right font-bold text-purple-600">
                                {r.blockchain.direction === 'in' ? '+' : '-'}{fmtNum(bcAmt)}
                              </td>
                              <td className="px-4 py-3 text-right font-bold text-amber-600">
                                {fmtNum(Math.abs(manualAmt - bcAmt))}
                              </td>
                              <td className="px-4 py-3 text-xs text-amber-700">
                                {r.diffs.map(d => d.field).join(', ')}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ========== Blockchain Only (ขาด manual) ========== */}
              {reconcileData.blockchain_only.length > 0 && (
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-theme overflow-hidden mb-6">
                  <div className="px-5 py-3 border-b border-red-100 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
                    <h4 className="text-sm font-bold text-red-700 dark:text-red-400 flex items-center gap-2">
                      <FontAwesomeIcon icon={faTimesCircle} />
                      ขาด — มีบน Blockchain แต่ไม่มี Manual ({reconcileData.blockchain_only.length} รายการ)
                    </h4>
                    <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">รายการที่ sync จาก blockchain แล้วแต่ยังไม่มีการบันทึก manual</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-slate-700/50 border-b border-gray-100 dark:border-slate-700">
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">TX Hash</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">Wallet</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">Direction</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">Amount (USDT)</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50 dark:divide-slate-700">
                        {reconcileData.blockchain_only.map((tx, idx) => (
                          <tr key={idx} className="bg-red-50/30 dark:bg-red-900/10 hover:bg-red-50/50 dark:hover:bg-red-900/20 transition-colors">
                            <td className="px-4 py-3">
                              {tx.tx_hash ? (
                                <a href={`https://tronscan.org/#/transaction/${tx.tx_hash}`} target="_blank" rel="noreferrer"
                                  className="text-xs hover:underline font-mono" style={{ color: 'var(--theme-primary)' }}>
                                  {tx.tx_hash.slice(0, 14)}...
                                </a>
                              ) : '-'}
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-500">
                              {tx.usdt_wallets?.label || shortAddr(tx.usdt_wallets?.address || '')}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1 ${
                                tx.direction === 'in' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                              }`}>
                                <FontAwesomeIcon icon={tx.direction === 'in' ? faArrowDown : faArrowUp} />
                                {tx.direction.toUpperCase()}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className={`font-bold ${tx.direction === 'in' ? 'text-green-600' : 'text-red-600'}`}>
                                {tx.direction === 'in' ? '+' : '-'}{fmtNum(parseFloat(String(tx.amount)))}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <p className="text-xs text-gray-700">{fmtDate(tx.created_at)}</p>
                              <p className="text-xs text-gray-400">{fmtTime(tx.created_at)}</p>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ========== Manual Only (เกิน) ========== */}
              {reconcileData.manual_only.length > 0 && (
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-theme overflow-hidden mb-6">
                  <div className="px-5 py-3 border-b border-orange-100 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20">
                    <h4 className="text-sm font-bold text-orange-700 dark:text-orange-400 flex items-center gap-2">
                      <FontAwesomeIcon icon={faQuestionCircle} />
                      เกิน — มี Manual แต่ไม่มีบน Blockchain ({reconcileData.manual_only.length} รายการ)
                    </h4>
                    <p className="text-xs text-orange-600 dark:text-orange-400 mt-0.5">รายการ manual ที่ไม่มี TX Hash ตรงกับ blockchain หรือไม่มี hash</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-slate-700/50 border-b border-gray-100 dark:border-slate-700">
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">TX Hash</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">Wallet</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">Direction</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">Amount (USDT)</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">Note</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50 dark:divide-slate-700">
                        {reconcileData.manual_only.map((tx, idx) => (
                          <tr key={idx} className="bg-orange-50/30 dark:bg-orange-900/10 hover:bg-orange-50/50 dark:hover:bg-orange-900/20 transition-colors">
                            <td className="px-4 py-3">
                              {tx.tx_hash ? (
                                <a href={`https://tronscan.org/#/transaction/${tx.tx_hash}`} target="_blank" rel="noreferrer"
                                  className="text-xs hover:underline font-mono" style={{ color: 'var(--theme-primary)' }}>
                                  {tx.tx_hash.slice(0, 14)}...
                                </a>
                              ) : <span className="text-xs text-gray-300">ไม่มี hash</span>}
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-500">
                              {tx.usdt_wallets?.label || shortAddr(tx.usdt_wallets?.address || '')}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1 ${
                                tx.direction === 'in' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                              }`}>
                                <FontAwesomeIcon icon={tx.direction === 'in' ? faArrowDown : faArrowUp} />
                                {tx.direction.toUpperCase()}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className={`font-bold ${tx.direction === 'in' ? 'text-green-600' : 'text-red-600'}`}>
                                {tx.direction === 'in' ? '+' : '-'}{fmtNum(parseFloat(String(tx.amount)))}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-500 max-w-[120px] truncate" title={tx.note || ''}>
                              {tx.note || '-'}
                            </td>
                            <td className="px-4 py-3">
                              <p className="text-xs text-gray-700">{fmtDate(tx.created_at)}</p>
                              <p className="text-xs text-gray-400">{fmtTime(tx.created_at)}</p>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ========== Matched Section ========== */}
              {reconcileData.matched.length > 0 && (
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-theme overflow-hidden mb-6">
                  <div className="px-5 py-3 border-b border-green-100 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
                    <h4 className="text-sm font-bold text-green-700 dark:text-green-400 flex items-center gap-2">
                      <FontAwesomeIcon icon={faCheckCircle} />
                      ตรงกัน ({reconcileData.matched.length} รายการ)
                    </h4>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-slate-700/50 border-b border-gray-100 dark:border-slate-700">
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">TX Hash</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">Wallet</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">Direction</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">Amount (USDT)</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50 dark:divide-slate-700">
                        {reconcileData.matched.map((r, idx) => (
                          <tr key={idx} className="hover:bg-green-50/50 dark:hover:bg-green-900/10 transition-colors">
                            <td className="px-4 py-3">
                              <a href={`https://tronscan.org/#/transaction/${r.blockchain.tx_hash}`} target="_blank" rel="noreferrer"
                                className="text-xs hover:underline font-mono" style={{ color: 'var(--theme-primary)' }}>
                                {r.blockchain.tx_hash?.slice(0, 14)}...
                              </a>
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-500">
                              {r.blockchain.usdt_wallets?.label || shortAddr(r.blockchain.usdt_wallets?.address || '')}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1 ${
                                r.blockchain.direction === 'in' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                              }`}>
                                <FontAwesomeIcon icon={r.blockchain.direction === 'in' ? faArrowDown : faArrowUp} />
                                {r.blockchain.direction.toUpperCase()}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className={`font-bold ${r.blockchain.direction === 'in' ? 'text-green-600' : 'text-red-600'}`}>
                                {r.blockchain.direction === 'in' ? '+' : '-'}{fmtNum(parseFloat(String(r.blockchain.amount)))}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <p className="text-xs text-gray-700">{fmtDate(r.blockchain.created_at)}</p>
                              <p className="text-xs text-gray-400">{fmtTime(r.blockchain.created_at)}</p>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* All clear */}
              {reconcileData.summary.mismatched === 0 && reconcileData.summary.blockchain_only === 0 && reconcileData.summary.manual_only === 0 && (
                <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-8 text-center border border-green-100 dark:border-green-800">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-4xl text-green-500 dark:text-green-400 mb-3" />
                  <p className="text-green-700 dark:text-green-400 font-semibold">ข้อมูลตรงกันทั้งหมด ไม่มีรายการขาด/เกิน</p>
                </div>
              )}
            </>
          ) : (
            <div className={`${CARD_CLASS} p-16 text-center`}>
              <FontAwesomeIcon icon={faBalanceScale} className="text-5xl text-gray-200 dark:text-slate-600 mb-4" />
              <p className="text-gray-400 dark:text-slate-500 text-sm">กดปุ่ม "ตรวจสอบใหม่" เพื่อเริ่มเทียบข้อมูล Manual vs Blockchain</p>
            </div>
          )}
        </>
      )}

      <UiModal
        open={showAddWallet}
        onClose={() => !submitting && setShowAddWallet(false)}
        title="เพิ่ม USDT Wallet"
        icon={faWallet}
        iconStyle={{ color: 'var(--theme-primary)' }}
        size="md"
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={() => setShowAddWallet(false)} disabled={submitting} className="flex-1 justify-center">
              ยกเลิก
            </UiButton>
            <UiButton icon={faCheckCircle} onClick={handleAddWallet} disabled={!newAddress || submitting} loading={submitting} className="flex-1 justify-center">
              {submitting ? 'กำลังเพิ่ม...' : 'เพิ่ม Wallet'}
            </UiButton>
          </div>
        }
      >
        <div>
          <label className={LABEL_CLASS}>Wallet Address (TRC20) <span className="text-red-500">*</span></label>
          <input type="text" value={newAddress} onChange={(e) => setNewAddress(e.target.value)}
            placeholder="T..." className={`${INPUT_CLASS} font-mono`} />
        </div>
        <div>
          <label className={LABEL_CLASS}>Label</label>
          <input type="text" value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
            placeholder="e.g. Wallet หลัก" className={INPUT_CLASS} />
        </div>
      </UiModal>

      <UiModal
        open={showRecordTx}
        onClose={() => !submitting && setShowRecordTx(false)}
        title="บันทึกรายการ USDT"
        icon={faPlus}
        iconStyle={{ color: 'var(--theme-primary)' }}
        size="md"
        scrollable
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={() => setShowRecordTx(false)} disabled={submitting} className="flex-1 justify-center">
              ยกเลิก
            </UiButton>
            <UiButton icon={faCheckCircle} onClick={handleRecordTx} disabled={!txWalletId || !txAmount || submitting} loading={submitting} className="flex-1 justify-center">
              {submitting ? 'กำลังบันทึก...' : 'บันทึกรายการ'}
            </UiButton>
          </div>
        }
      >
        <div>
          <label className={LABEL_CLASS}>Wallet <span className="text-red-500">*</span></label>
          <select value={txWalletId} onChange={(e) => setTxWalletId(e.target.value)} className={INPUT_CLASS}>
            <option value="">เลือก Wallet</option>
            {wallets.map((w) => (
              <option key={w.id} value={w.id}>{w.label || shortAddr(w.address)} ({fmtNum(parseFloat(String(w.last_balance)))} USDT)</option>
            ))}
          </select>
        </div>
        <div>
          <label className={LABEL_CLASS}>Direction <span className="text-red-500">*</span></label>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setTxDirection('in')}
              className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition-colors ${
                txDirection === 'in'
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-500 dark:border-green-600 text-green-700 dark:text-green-400'
                  : 'border-gray-200 dark:border-slate-600 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-700'
              }`}
            >
              <FontAwesomeIcon icon={faArrowDown} className="mr-1" /> รับเข้า (IN)
            </button>
            <button
              type="button"
              onClick={() => setTxDirection('out')}
              className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition-colors ${
                txDirection === 'out'
                  ? 'bg-red-50 dark:bg-red-900/20 border-red-500 dark:border-red-600 text-red-700 dark:text-red-400'
                  : 'border-gray-200 dark:border-slate-600 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-700'
              }`}
            >
              <FontAwesomeIcon icon={faArrowUp} className="mr-1" /> ส่งออก (OUT)
            </button>
          </div>
        </div>
        <div>
          <label className={LABEL_CLASS}>Amount (USDT) <span className="text-red-500">*</span></label>
          <input type="number" step="0.01" value={txAmount} onChange={(e) => setTxAmount(e.target.value)}
            placeholder="0.00" className={INPUT_CLASS} />
        </div>
        <div>
          <label className={LABEL_CLASS}>TX Hash (optional)</label>
          <input type="text" value={txHash} onChange={(e) => setTxHash(e.target.value)}
            placeholder="Transaction hash..." className={`${INPUT_CLASS} font-mono`} />
        </div>
        <div>
          <label className={LABEL_CLASS}>Note (optional)</label>
          <textarea value={txNote} onChange={(e) => setTxNote(e.target.value)} rows={2}
            placeholder="หมายเหตุ..." className={TEXTAREA_CLASS} />
        </div>
      </UiModal>
    </div>
  );
};

export default UsdtWalletPage;
